import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Ticket,
  Clock,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Users,
  Activity
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

export default function SupportDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.get(`${backendUrl}/api/support/stats`);
      setStats(res.data);
    } catch (error) {
      console.error('Erreur chargement stats support:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="support-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Centre Support</h1>
        <p className="text-muted-foreground">Gestion des demandes et interventions</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Total Tickets */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Tickets</p>
                <h3 className="text-2xl font-bold mt-2">{stats?.total_tickets || 0}</h3>
              </div>
              <Ticket className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        {/* Open Tickets */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Tickets Ouverts</p>
                <h3 className="text-2xl font-bold mt-2">{stats?.open_tickets || 0}</h3>
                {stats?.urgent_tickets > 0 && (
                  <p className="text-xs text-destructive mt-1">
                    {stats.urgent_tickets} urgent{stats.urgent_tickets > 1 ? 's' : ''}
                  </p>
                )}
              </div>
              <AlertCircle className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        {/* In Progress */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">En Cours</p>
                <h3 className="text-2xl font-bold mt-2">{stats?.in_progress_tickets || 0}</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Mes tickets : {stats?.my_tickets || 0}
                </p>
              </div>
              <Clock className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        {/* Resolved */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Résolus</p>
                <h3 className="text-2xl font-bold mt-2">{stats?.resolved_tickets || 0}</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Actions aujourd'hui : {stats?.actions_today || 0}
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/admin/support/tickets?status=open')}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              Tickets à Traiter
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {stats?.open_tickets || 0} ticket{stats?.open_tickets > 1 ? 's' : ''} en attente d'attribution
            </p>
            <Button variant="outline" className="mt-4 w-full">
              Voir les tickets ouverts
            </Button>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/admin/support/tickets?assigned_to_me=true')}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-500" />
              Mes Tickets
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {stats?.my_tickets || 0} ticket{stats?.my_tickets > 1 ? 's' : ''} qui vous sont assignés
            </p>
            <Button variant="outline" className="mt-4 w-full">
              Voir mes tickets
            </Button>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/admin/support/actions')}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              Journal d'Interventions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {stats?.actions_today || 0} action{stats?.actions_today > 1 ? 's' : ''} effectuées aujourd'hui
            </p>
            <Button variant="outline" className="mt-4 w-full">
              Voir le journal
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Urgent Tickets Alert */}
      {stats?.urgent_tickets > 0 && (
        <Card className="border-destructive bg-destructive/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Attention : Tickets Urgents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              {stats.urgent_tickets} ticket{stats.urgent_tickets > 1 ? 's' : ''} marqué{stats.urgent_tickets > 1 ? 's' : ''} comme urgent{stats.urgent_tickets > 1 ? 's' : ''} nécessite{stats.urgent_tickets > 1 ? 'nt' : ''} une attention immédiate.
            </p>
            <Button variant="destructive" onClick={() => navigate('/admin/support/tickets?priority=urgent')}>
              Traiter les tickets urgents
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
